import { addDoc, collection } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import React, { useState } from "react";
import { db, storage } from "../../firebase";
import StyledButton from "./components/button";
import StyledInput from "./components/input";
import StyledDropdown from "./fitness/components/dropdown";

interface BookData {
  title: string;
  author: string;
  rating: string;
  comments: string;
  date: string;
  status: string;
  type: string;
  cover: string;
}

interface Props {
  onUpload?: (book: BookData) => void;
}

const BookUpload: React.FC<Props> = ({ onUpload }) => {
  const [adminPassword, setAdminPassword] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [rating, setRating] = useState("");
  const [comments, setComments] = useState("");
  const [date, setDate] = useState("");
  const [status, setStatus] = useState("Finished book");
  const [type, setType] = useState("book");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const uploadImage = async (file: File): Promise<string> => {
    console.log("Uploading file:", file);
    const fileRef = ref(storage, `book-covers/${file.name}`);
    try {
      const result = await uploadBytes(fileRef, file);
      console.log("Upload successful:", result);
      const url = await getDownloadURL(fileRef);
      console.log("Download URL:", url);
      return url;
    } catch (err) {
      console.error("Upload failed:", err);
      throw err;
    }
  };

  const handleSubmit = async () => {
    if (!imageFile) {
      setError("Please select a book cover image");
      return;
    }

    try {
      setUploading(true);
      const imageUrl = await uploadImage(imageFile);
      const book: BookData = {
        title,
        author,
        rating,
        comments,
        date,
        status,
        type,
        cover: imageUrl,
      };

      await addDoc(collection(db, "books"), book); // Save to Firestore
      onUpload?.(book);

      // Reset form
      setTitle("");
      setAuthor("");
      setRating("");
      setComments("");
      setDate("");
      setStatus("Finished book");
      setType("book");
      setImageFile(null);
      setAdminPassword("");
      setIsAuthorized(false);
      setError("Book uploaded successfully!");
    } catch (e) {
      setError("Failed to upload book");
    } finally {
      setUploading(false);
    }
  };

  const handlePasswordCheck = () => {
    if (adminPassword === "154253") {
      setIsAuthorized(true);
      setError("");
    } else {
      setError("Invalid admin password");
    }
  };

  const fieldWrapperStyle = { margin: "10px 0" };

  return (
    <div style={{ margin: "20px 0" }}>
      <h3>Add a New Book</h3>
      {!isAuthorized ? (
        <>
          <div style={fieldWrapperStyle}>
            <StyledInput
              type="password"
              placeholder="Admin password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
            />
          </div>
          <div style={fieldWrapperStyle}>
            <StyledButton onClick={handlePasswordCheck}>Enter</StyledButton>
          </div>
        </>
      ) : (
        <>
          <div style={fieldWrapperStyle}>
            <StyledInput
              type="text"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div style={fieldWrapperStyle}>
            <StyledInput
              type="text"
              placeholder="Author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
            />
          </div>
          <div style={fieldWrapperStyle}>
            <StyledInput
              type="text"
              placeholder="Rating (e.g. 4.5/5)"
              value={rating}
              onChange={(e) => setRating(e.target.value)}
            />
          </div>
          <div style={fieldWrapperStyle}>
            <StyledInput
              type="text"
              placeholder="Comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
          <div style={fieldWrapperStyle}>
            <StyledInput
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div style={fieldWrapperStyle}>
            <StyledDropdown
              value={status}
              onChange={setStatus}
              options={[
                "Finished book",
                "Finished audiobook",
                "Still reading book",
                "Still listening audiobook",
              ]}
              placeholder="Select Status"
            />
          </div>
          <div style={fieldWrapperStyle}>
            <StyledDropdown
              value={type}
              onChange={setType}
              options={["book", "audiobook"]}
              placeholder="Select Type"
            />
          </div>
          <div style={fieldWrapperStyle}>
            <input
              type="file"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setImageFile(e.target.files[0]);
                }
              }}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={fieldWrapperStyle}>
            <StyledButton onClick={handleSubmit} disabled={uploading}>
              {uploading ? "Uploading..." : "Add Book"}
            </StyledButton>
          </div>
        </>
      )}
      {error && (
        <p
          style={{
            color: error.includes("successfully") ? "green" : "red",
            marginTop: "10px",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default BookUpload;
